import { useEffect, useMemo, useState } from "react";
import { GrStack, GrTypography } from "@jnpmedi/grebob";
import { GrFileUploadProgress } from "@jnpmedi/grebob/dist/components/surface/GrFileUploadProgress";
import { LoLang } from "@jnpmedi/helper-session";
import { getDocsFileIconName, isInvalidFileType } from "@newLibs/converting";
import { isFileSizeExceedsLimit, langHelper, MvnMessage } from "@newLibs/utils";
import {
  ARCHIVER_UPLOAD_CALLBACK_INVALID_QUERY_KEYS,
  DEFAULT_DOCUMENT_FILE_UPLOAD_CALLBACK_INVALID_QUERY_KEYS,
  FileUploadProgressItemWithFile,
  useBulkFileUpload,
} from "@newHooks/common/useBulkFileUpload";
import { enqueueSnackbar } from "@newHooks/context/snackbar/notification.context";
import { notify } from "@newHooks/context/snackbar/notify";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { DOCS_COMMON_LANG } from "@maven-msg/lang/docs/common";
import { DOCS_DOCUMENT_LANG } from "@maven-msg/lang/docs/document";
import { FolderType } from "@maven-rest/docs/Folder.dto";
import { FolderFileCriteria } from "@maven-rest/docs/FolderFile.dto";
import { RefResourceType, RefServiceType } from "@maven-rest/ref/Ref.dto";
import { convertFileTypeToS3ResourceContentType } from "@maven-rest/resource/S3.lib";

interface AttachmentUploadFloatingBarProps {
  organizationId: bigint;
  folderType: FolderType;
  uploadSizeLimitInMB: number;
  uploadSizeLimitInMBForHostOS: number;
  files: Array<File>;
  folderId?: bigint;
  onClose: () => void;
}

export function DropSectionProgressBar({
  organizationId,
  folderType,
  uploadSizeLimitInMB,
  uploadSizeLimitInMBForHostOS,
  files,
  folderId,
  onClose,
}: AttachmentUploadFloatingBarProps) {
  const lang = langHelper.getLang();

  const [fileUploadResultSummary, setFileUploadResultSummary] = useState<{ success: number; failed: number }>({ success: 0, failed: 0 });

  const {
    fileUploadList,
    setFileUploadList,
    createPresignedPostUrlBulk,
    isCreateDocumentFilesLoading,
    isCreatePresignedPostUrlBulkLoading,
  } = useBulkFileUpload({
    organizationId,
    uploadSizeLimitInMB,
    uploadSizeLimitInMBForHostOS,
    folderType,
    folderId,
    isTableDndBulkUpload: true,
    invalidQueryKeys:
      folderType === FolderType.ARCHIVER
        ? ARCHIVER_UPLOAD_CALLBACK_INVALID_QUERY_KEYS
        : DEFAULT_DOCUMENT_FILE_UPLOAD_CALLBACK_INVALID_QUERY_KEYS,
    onFolderFileCreateFailed: (failedCount) => setFileUploadResultSummary((prev) => ({ ...prev, failed: prev.failed + failedCount })),
    onFolderFileCreateSuccess: handleUploadResultUpdate,
  });

  const isUploadActionLoading = isCreateDocumentFilesLoading || isCreatePresignedPostUrlBulkLoading;

  const handleFloatingBarClose = () => {
    if (isUploadActionLoading) {
      enqueueSnackbar({ message: "File Upload on Processing...", severity: "warning" });
      return;
    }

    onClose();
  };

  function handleUploadResultUpdate(res?: { success: number; failed: number }) {
    if (!res) return;

    const { success, failed } = fileUploadResultSummary;

    setFileUploadResultSummary({ success: res.success + success, failed: failed + res.failed });
  }

  useEffect(() => {
    const fileListWithValidationMark = markAnsSortInvalidFiles(files, {
      uploadSizeLimitInMB,
      uploadSizeLimitInMBForHostOS,
      folderType,
      isErrorNotify: true,
    });

    const newFileUploadList: Array<FileUploadProgressItemWithFile> = fileListWithValidationMark.map(({ file, invalidReason }) => ({
      file,
      name: getFileNameWithoutExt(file.name.normalize("NFC")),
      type: getDocsFileIconName(file.type.toLowerCase()),
      progress: 0,
      status: !invalidReason ? "UPLOADING" : "ERROR",
    }));

    setFileUploadList([...newFileUploadList, ...fileUploadList]);

    const validFileTypes = fileListWithValidationMark
      .filter(({ invalidReason }) => !invalidReason)
      .map(({ file }) => ({
        name: file.name.normalize("NFC"),
        resourceType: RefResourceType.Docs_FolderFile,
        contentType: convertFileTypeToS3ResourceContentType(file.type.toLowerCase()),
      }));

    const uploadFailedCount = newFileUploadList.length - validFileTypes.length;
    handleUploadResultUpdate({ success: 0, failed: uploadFailedCount });

    if (!validFileTypes.length) {
      return;
    }

    createPresignedPostUrlBulk({
      fileTypes: validFileTypes,
      organizationId,
      folderType,
      service: RefServiceType.Docs,
    });

    return () => {
      setFileUploadList([]);
      setFileUploadResultSummary({ success: 0, failed: 0 });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const uploadDoneTitleMemo = useMemo(() => {
    const { success, failed } = fileUploadResultSummary;
    const isDelimiterNeed = !!success && !!failed;
    return (
      <GrStack gap={2} alignItems="center">
        {!!success && (
          <GrStack>
            <GrTypography type="Content.Subtitle03" color="darkNavy040">
              {success}
              {MvnMessage(COMMON_LANG.COUNT_SUFFIX)}
            </GrTypography>
            &nbsp;
            <GrTypography color="grey090" type="Content.Subtitle03">
              {MvnMessage(DOCS_COMMON_LANG.UPLOAD_COMPLETED)}
            </GrTypography>
          </GrStack>
        )}
        {isDelimiterNeed && (
          <GrTypography type="Content.Subtitle03" color="grey090">
            /
          </GrTypography>
        )}
        {!!failed && (
          <GrStack>
            <GrTypography type="Content.Subtitle03" color="darkNavy040">
              {failed}
              {MvnMessage(COMMON_LANG.COUNT_SUFFIX)}
            </GrTypography>
            &nbsp;
            <GrTypography color="grey090" type="Content.Subtitle03">
              {MvnMessage(DOCS_COMMON_LANG.UPLOAD_FAILED)}
            </GrTypography>
          </GrStack>
        )}
      </GrStack>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUploadResultSummary, lang]);

  // const uploadProcessingTitleMemo = useMemo(() => {
  //   return (
  //     <GrStack>
  //       <GrTypography type="Content.Subtitle03" color="darkNavy040">
  //         {fileUploadList.length}
  //         {MvnMessage(COMMON_LANG.COUNT_SUFFIX)}
  //       </GrTypography>
  //       &nbsp;
  //       <GrTypography color="grey090" type="Content.Subtitle03">
  //         {MvnMessage(DOCS_COMMON_LANG.UPLOAD_PROCESSING)}
  //       </GrTypography>
  //     </GrStack>
  //   );
  // }, [fileUploadList.length]);

  return (
    <GrFileUploadProgress
      open
      lang={lang === LoLang.EN ? "EN" : "KR"}
      title={isUploadActionLoading ? undefined : uploadDoneTitleMemo}
      listItems={fileUploadList}
      onClose={handleFloatingBarClose}
    />
  );
}

// belows are utility functions...
export function getFileNameWithoutExt(fileName: string): string {
  if (!fileName.length) return "";
  const lastDotIndex = fileName.lastIndexOf(".");

  if (lastDotIndex <= 0) {
    return fileName;
  }

  return fileName.substring(0, lastDotIndex);
}

export function markAnsSortInvalidFiles(
  fileList: Array<File>,
  options?: { uploadSizeLimitInMB: number; uploadSizeLimitInMBForHostOS: number; folderType: FolderType; isErrorNotify: boolean }
): Array<{ file: File; invalidReason?: "EXT" | "SIZE" | "UNKNOWN" }> {
  if (!fileList?.length) {
    return [];
  }

  const { uploadSizeLimitInMB, uploadSizeLimitInMBForHostOS, folderType, isErrorNotify } = !!options
    ? options
    : {
        uploadSizeLimitInMB: FolderFileCriteria.FOLDER_FILE_MAX_UPLOAD_LIMIT_IN_TEN_MIB,
        uploadSizeLimitInMBForHostOS: FolderFileCriteria.FOLDER_FILE_MAX_UPLOAD_LIMIT_IN_TEN_MIB,
        folderType: FolderType.NORMAL,
        isErrorNotify: false,
      };

  const copyFileList: Array<{ file: File; invalidReason?: "EXT" | "SIZE" | "UNKNOWN" }> = [];
  const accept =
    folderType === FolderType.NORMAL ? FolderFileCriteria.FOLDER_FILE_UPLOAD_ACCEPT : FolderFileCriteria.FOLDER_FILE_ARCHIVER_UPLOAD_ACCEPT;

  for (const file of fileList) {
    if (file) {
      if (!file.size) {
        isErrorNotify && notify(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_TABLE_EMPTY_TITLE), "WARN");
        continue;
      }

      if (isFileSizeExceedsLimit(file, uploadSizeLimitInMB)) {
        isErrorNotify &&
          notify(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_EXCEEDS_MAX_SIZE, { limit: uploadSizeLimitInMBForHostOS }), "WARN");
        copyFileList.push({ file, invalidReason: "SIZE" });
        continue;
      }

      if (isInvalidFileType(file, accept)) {
        isErrorNotify &&
          enqueueSnackbar({
            message: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_FILE_UPLOAD_EXTENSION_HELPER_TEXT, {
              ext: accept.replaceAll(".", " ").toUpperCase(),
            }),
            severity: "danger",
          });

        copyFileList.push({ file, invalidReason: "EXT" });
        continue;
      }

      copyFileList.push({ file, invalidReason: undefined });
    }
  }

  // NOTE: 업로드 시도도차 하지 않을 파일 앞으로 정렬.
  return copyFileList.sort((a, b) => {
    if (a.invalidReason && !b.invalidReason) return 1;
    if (!a.invalidReason && b.invalidReason) return -1;
    return 0;
  });
}
